import React, { useContext, useEffect, useState } from 'react';

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { Icon } from '../common/Icon';
import TextDiff from '../test-sets/TextDiff';
import TrimSpec from '../lego/trims/TrimSpec';
import JSONPreview from '../common/JSONPreview';
import { applyMongoUpdate, diffKeys } from '../ai/mongoUpdateUtils';

// Generic, collection-agnostic task editor: review a proposed edit or create on ONE document and
// approve/reject it. `input.service` is any standard Feathers CRUD service. The editor fetches via
// `find({ query: { [keyField]: targetId } })` (keyField defaults to '_id'), edits via
// `update(_id, mongoUpdate)`, and inserts via `create(doc)` — stamping updatedBy/createdBy itself.
// So a plain MongooseCRUDService works out of the box, and models just point at the existing
// ModelService (`services/data/models/all`, keyField 'idModelo') whose writes already go through the
// Model WrappedDataObject (→ cache reload + view invalidation). Rendering is generic (the mongo
// update + diff/JSON); ids show as plain code, or as a TrimSpec pill when `input.idSpec` is set.
const CONFIDENCE_LEVEL = { high: 'success', med: 'warning', medium: 'warning', low: 'danger' };

const Label = ({ children, className = '' }) => (
  <span className={`text-secondary text-uppercase small ${className}`}>{children}</span>
);

// A bordered panel — the recurring `border rounded p-2 …` box.
const Box = ({ className = '', children }) => (
  <div className={`border rounded p-2 ${className}`}>{children}</div>
);

// Render an id: as a TrimSpec pill when the task declares an `idSpec` (e.g. 'modelId'),
// otherwise as plain code. Keeps the editor generic while restoring the nice pill per-domain.
const IdView = ({ id, spec }) => (spec
  ? <TrimSpec spec={spec} value={id} />
  : <code className={'monospace'}>{id || '—'}</code>);

// Read-only context doc: id + click to expand the full document.
function DocCard({ doc, id, spec }) {
  const [open, setOpen] = useState(false);
  if (!doc || doc.__missing) {
    return <Box className={'mb-2 bg-light-danger small'}><IdView id={id} spec={spec} /> — not found</Box>;
  }
  return <Box className={'mb-2 bg-light small'}>
    <div className={'cursor-pointer'} onClick={() => setOpen((o) => !o)}>
      <Icon small icon={open ? 'expand_less' : 'expand_more'} className={'mr-1'} />
      <IdView id={doc.idModelo || doc._id || id} spec={spec} />
    </div>
    {open && <div className={'mt-2 border-top pt-2'}><JSONPreview value={doc} /></div>}
  </Box>;
}

export default function TaskEditorDocumentEdit({ batchEditor, task }) {
  const { page } = useContext(LegoAdminPageContext);

  const {
    op = 'edit', title, headline, service, keyField = '_id', targetId, update, doc,
    contextIds = [], reason, confidence, idSpec,
  } = task.input || {};
  const isCreate = op === 'create';
  const svc = service ? page.service(service) : null;

  const [target, setTarget] = useState(null);
  const [contextDocs, setContextDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showDoc, setShowDoc] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { page.runAsync(loadData()); }, [task._id]);

  // Fetch one doc by the task's keyField (default _id) via the service's standard `find`.
  async function fetchByKey(id) {
    const res = await svc.find({ query: { [keyField]: id } });
    return ((res && res.data) || res || [])[0] || null;
  }

  async function loadData() {
    setError(null);
    if (!svc) { setError('Task input has no "service".'); setLoaded(true); return; }
    const [tgt, ctx] = await Promise.all([
      targetId ? fetchByKey(targetId).catch(() => null) : null,
      Promise.all((contextIds || []).map((id) => fetchByKey(id)
        .then((d) => d || { __missing: true, [keyField]: id })
        .catch(() => ({ __missing: true, [keyField]: id })))),
    ]);
    setTarget(tgt);
    setContextDocs(ctx);
    setLoaded(true);
  }

  const decision = task.output?.decision;

  // Build the before/after for the diff. `update` is a full mongo update object ($set, $unset,
  // $pull, …); `applyMongoUpdate` previews its effect.
  let before = null;
  let after = null;
  if (isCreate) {
    before = {};
    after = doc || {};
  } else if (update && Object.keys(update).length) {
    before = target || {};
    after = target ? applyMongoUpdate(target, update) : null;
  }
  const changedKeys = (before && after) ? diffKeys(before, after) : [];
  const canApply = isCreate ? !!doc : (!!update && !!target);

  const diffBlock = (
    <Box className={'bg-white'}>
      {changedKeys.length
        ? changedKeys.map((k) => <div key={k} className={'small mb-1'}>
            <div className={'text-secondary'}>{k}</div>
            <TextDiff type={'json'} inputA={before[k]} inputB={after[k]} ellipsis={200} />
          </div>)
        : <span className={'text-muted small'}>No change.</span>}
    </Box>
  );

  async function onApprove() {
    setBusy(true);
    setError(null);
    try {
      const sig = page.getLoggedUserSignature();
      if (isCreate) {
        await svc.create({ ...doc, createdBy: sig, updatedBy: sig });
      } else {
        await svc.update(target._id, { ...update, $set: { ...(update.$set || {}), updatedBy: sig } });
      }
      await batchEditor.doneAndNext({ decision: 'approved', op });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  // Rejection always records why: saved as a task note (the comment thread) and in output.reason.
  async function confirmReject() {
    const text = rejectReason.trim();
    if (!text) return;
    const note = { content: text, userId: page.getLoggedUserSignature().id, createdAt: new Date() };
    await batchEditor.updateNotes([...(task.notes || []), note]);
    await batchEditor.doneAndNext({ decision: 'rejected', reason: text });
  }

  if (!loaded && !error) {
    return <div className={'text-white full-screen-center'}>Loading…</div>;
  }

  const confLevel = CONFIDENCE_LEVEL[(confidence || '').toLowerCase()] || 'secondary';
  const existsConflict = isCreate && !!target;
  const missingTarget = !isCreate && loaded && !!targetId && !target;

  return <div className={'grid-pdf-extraction'}>
    <div className={'bg-white overflow-auto p-3'}>
      <h5 className={'d-flex align-items-center'}>
        <span className={'mr-2'}>{title || (isCreate ? 'Create document' : 'Document edit')}:</span>
        <IdView id={targetId} spec={idSpec} />
        {headline && <ins className={'ml-2 px-1 rounded'}>{headline}</ins>}
        {target && <Icon icon={showDoc ? 'expand_less' : 'expand_more'} className={'ml-2 cursor-pointer'}
          title={'Show full document'} onClick={() => setShowDoc((s) => !s)} />}
      </h5>
      {showDoc && target && <Box className={'mb-2 bg-light'}><JSONPreview value={target} /></Box>}

      {(reason || confidence) && <div className={'alert alert-info py-2 my-2'}>
        <div className={'mb-1'}>
          <Label className={'mr-2'}>confidence</Label>
          <span className={`badge badge-${confLevel}`}>{(confidence || '?').toUpperCase()}</span>
        </div>
        {reason && <div><Label className={'mr-2'}>reason</Label>{reason}</div>}
      </div>}

      {isCreate
        ? <div className={'mt-3'}>
            <Label className={'d-block mb-1'}>New document</Label>
            {diffBlock}
          </div>
        : <div className={'row no-gutters mt-3'}>
            <div className={'col-5 pr-2'}>
              <Label className={'d-block mb-1'}>Update</Label>
              <pre className={'border rounded p-2 bg-light small mb-0'} style={{ whiteSpace: 'pre-wrap' }}>
                {update ? JSON.stringify(update, null, 2) : '— nothing to apply —'}
              </pre>
            </div>
            <div className={'col-7 pl-2'}>
              <Label className={'d-block mb-1'}>Diff preview</Label>
              {diffBlock}
            </div>
          </div>}

      {contextDocs.length > 0 && <div className={'mt-4'}>
        <Label className={'d-block mb-1'}>Context ({contextDocs.length})</Label>
        {contextDocs.map((d, i) => <DocCard key={(d && (d.idModelo || d._id)) || i} doc={d} id={contextIds[i]} spec={idSpec} />)}
      </div>}
    </div>

    <div className={'p-3 bg-light overflow-auto'}>
      <div className={'text-center mb-3'}>
        {missingTarget && <div className={'alert alert-danger py-2 small'}>Target <IdView id={targetId} spec={idSpec} /> not found.</div>}
        {existsConflict && <div className={'alert alert-warning py-2 small'}><IdView id={targetId} spec={idSpec} /> already exists — approving will fail.</div>}
        {!canApply && !missingTarget && <div className={'alert alert-danger py-2 small'}>Nothing to apply.</div>}

        {decision && <div className={`alert alert-${decision === 'approved' ? 'success' : 'secondary'} py-1 small`}>
          Last decision: <strong>{decision}</strong>
        </div>}

        {error && <div className={'alert alert-danger py-2 small text-left'}>{error}</div>}

        <button className={'btn btn-success btn-block'} disabled={busy || !canApply}
          onClick={() => page.runAsync(onApprove)}>
          <Icon icon={'done'} /> {busy ? 'Applying…' : (isCreate ? 'Approve & create' : 'Approve & apply')}
        </button>

        {rejecting
          ? <div className={'mt-1'}>
              <textarea className={'form-control form-control-sm'} rows={2} autoFocus
                placeholder={'Why is this rejected? (required)'}
                value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
              <div className={'mt-1 d-flex'}>
                <button className={'btn btn-danger btn-sm flex-grow-1'} disabled={!rejectReason.trim()}
                  onClick={() => page.runAsync(confirmReject)}>
                  <Icon icon={'close'} /> Confirm reject
                </button>
                <button className={'btn btn-light btn-sm ml-2'}
                  onClick={() => { setRejecting(false); setRejectReason(''); }}>Cancel</button>
              </div>
            </div>
          : <button className={'btn btn-outline-danger btn-block'} disabled={busy}
              onClick={() => setRejecting(true)}>
              <Icon icon={'close'} /> Reject
            </button>}
      </div>

      <div className={'border border-left-0 border-right-0'}>
        {batchEditor.getTaskStateControls()}
      </div>
    </div>
  </div>;
}
